/**
 * Super Admin Login — redesigned to match reference design
 * All auth/integration logic is unchanged.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import styles from './SuperAdminLogin.module.css';
import './SuperAdminLoginShell.css';
import { apiService, getUploadsBaseUrl } from '../services/apiService';
import { RBACService } from '../utils/rbac';

const nacLogoUrl = `${process.env.PUBLIC_URL || ''}/nac_logo_tight_circle.png`;

interface LoginForm {
  email: string;
  password: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  general?: string;
}

/* ── Inline SVG icons (no external deps) ─────────────────────────── */
const EyeOpen = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeClosed = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 3l18 18" />
    <path d="M10.6 5.1A11 11 0 0 1 12 5c7 0 11 7 11 7a13.2 13.2 0 0 1-2.6 3.4M6.6 6.6A13 13 0 0 0 1 12s4 7 11 7a10.6 10.6 0 0 0 5-1.2" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3l8 4v5c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V7l8-4Z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
    strokeLinecap="round" strokeLinejoin="round" width="16" height="16" aria-hidden>
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

/* ── Lotus corner ornament ────────────────────────────────────────── */
const LotusCorner = ({ size = 110 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" aria-hidden>
    <g transform="translate(50,50)">
      {([0, 45, 90, 135, 180, 225, 270, 315] as number[]).map((deg) => (
        <ellipse key={deg} cx="0" cy="-26" rx="8" ry="16"
          fill="white" fillOpacity="0.14" transform={`rotate(${deg})`} />
      ))}
      <circle cx="0" cy="0" r="36" fill="none" stroke="white" strokeOpacity="0.2" strokeWidth="1.2" />
      <circle cx="0" cy="0" r="24" fill="none" stroke="white" strokeOpacity="0.15" strokeWidth="1" />
      <circle cx="0" cy="0" r="10" fill="white" fillOpacity="0.16" />
    </g>
  </svg>
);

/* ── Full page background decoration ─────────────────────────────── */
const PageDecoration = () => (
  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>

    {/* Diamond lattice pattern */}
    <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} aria-hidden>
      <defs>
        <pattern id="sa-diamond" width="56" height="56" patternUnits="userSpaceOnUse">
          <path d="M28 3 L53 28 L28 53 L3 28 Z"
            fill="none" stroke="white" strokeWidth="1.1" strokeOpacity="0.2" />
          <circle cx="28" cy="28" r="2.4" fill="white" fillOpacity="0.15" />
          <circle cx="0"  cy="0"  r="2.4" fill="white" fillOpacity="0.15" />
          <circle cx="56" cy="0"  r="2.4" fill="white" fillOpacity="0.15" />
          <circle cx="0"  cy="56" r="2.4" fill="white" fillOpacity="0.15" />
          <circle cx="56" cy="56" r="2.4" fill="white" fillOpacity="0.15" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#sa-diamond)" />
    </svg>

    {/* Corner lotus ornaments */}
    <div style={{ position: 'absolute', top: 12, left: 12 }}>
      <LotusCorner />
    </div>
    <div style={{ position: 'absolute', top: 12, right: 12, transform: 'scaleX(-1)' }}>
      <LotusCorner />
    </div>
    <div style={{ position: 'absolute', bottom: 12, left: 12, transform: 'scaleY(-1)' }}>
      <LotusCorner />
    </div>
    <div style={{ position: 'absolute', bottom: 12, right: 12, transform: 'scale(-1,-1)' }}>
      <LotusCorner />
    </div>

    {/* Druk dragon watermark — centred behind card */}
    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg viewBox="0 0 640 420" style={{ width: '78%', maxWidth: 780, opacity: 0.12 }}
        fill="white" aria-hidden>
        <path d="M 55 310 C 90 265 140 285 185 250 C 230 215 250 168 305 145 C 360 122 415 135 455 168 C 495 200 505 255 482 300 C 460 345 415 365 370 355 C 325 345 295 318 260 330 C 225 342 205 375 172 368 C 139 361 118 330 86 318 Z" />
        <path d="M 455 168 C 478 148 500 143 522 152 C 510 132 505 115 515 105 C 490 110 475 125 470 145 Z" />
        <ellipse cx="530" cy="148" rx="52" ry="40" />
        <path d="M 564 130 C 598 118 618 138 608 162 C 598 180 570 172 564 155 Z" />
        <path d="M 560 158 C 590 162 608 175 595 188 C 582 198 562 188 558 172 Z" />
        <path d="M 510 108 C 498 78 510 55 525 68 C 528 48 542 62 535 86 Z" />
        <path d="M 530 104 C 522 72 537 50 551 65 C 555 47 566 63 558 88 Z" />
        <path d="M 500 122 C 485 98 488 80 500 88 C 495 72 510 74 512 94 Z" />
        <circle cx="528" cy="142" r="9" />
        <circle cx="531" cy="140" r="4" fill="#FF8200" />
        <ellipse cx="598" cy="150" rx="6" ry="4" fillOpacity="0.5" />
        <path d="M 590 172 C 605 185 610 200 598 208" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" />
        <path d="M 578 178 C 588 198 580 215 565 218" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" />
        <path d="M 258 258 C 245 288 248 318 236 330" fill="none" stroke="white" strokeWidth="14" strokeLinecap="round" />
        <path d="M 236 328 L 218 355 M 236 328 L 248 355 M 236 328 L 260 348" stroke="white" strokeWidth="7" strokeLinecap="round" />
        <path d="M 368 188 C 354 218 356 248 344 262" fill="none" stroke="white" strokeWidth="14" strokeLinecap="round" />
        <path d="M 344 260 L 326 288 M 344 260 L 356 288 M 344 260 L 370 278" stroke="white" strokeWidth="7" strokeLinecap="round" />
        <path d="M 148 295 C 136 325 138 352 126 365" fill="none" stroke="white" strokeWidth="12" strokeLinecap="round" />
        <path d="M 126 363 L 108 388 M 126 363 L 138 388 M 126 363 L 152 382" stroke="white" strokeWidth="6" strokeLinecap="round" />
        <path d="M 390 330 C 378 358 380 382 368 395" fill="none" stroke="white" strokeWidth="12" strokeLinecap="round" />
        <path d="M 368 393 L 350 415 M 368 393 L 380 415 M 368 393 L 394 410" stroke="white" strokeWidth="6" strokeLinecap="round" />
        <path d="M 55 310 C 28 338 16 368 38 378 C 60 388 78 362 62 356 C 46 350 34 360 42 372" fill="none" stroke="white" strokeWidth="12" strokeLinecap="round" />
        <path d="M 170 268 C 183 255 200 262 194 275 C 208 258 228 267 220 280 C 235 262 258 272 248 285" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" />
        <path d="M 280 200 C 293 188 310 195 304 208 C 318 192 338 201 330 214 C 345 197 365 207 356 220" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" />
        <circle cx="218" cy="358" r="16" />
        <circle cx="218" cy="358" r="9" fill="rgba(255,130,0,0.6)" />
      </svg>
    </div>

  </div>
);

/* ── Avatar illustration ──────────────────────────────────────────── */
const AvatarIllustration = () => (
  <svg viewBox="0 0 120 120" fill="none" aria-hidden>
    {/* body / suit */}
    <ellipse cx="60" cy="95" rx="32" ry="20" fill="#2563eb" opacity="0.15" />
    <rect x="36" y="68" width="48" height="38" rx="12" fill="#1d4ed8" />
    {/* collar / tie area */}
    <path d="M52 68 L60 80 L68 68" fill="#fff" opacity="0.9" />
    <path d="M58 72 L60 82 L62 72" fill="#1e40af" />
    {/* neck */}
    <rect x="54" y="60" width="12" height="12" rx="4" fill="#fde8cc" />
    {/* head */}
    <circle cx="60" cy="48" r="20" fill="#fde8cc" />
    {/* hair */}
    <path d="M40 44 C40 28 80 28 80 44" fill="#92400e" />
    {/* eyes */}
    <circle cx="53" cy="47" r="2.5" fill="#1e293b" />
    <circle cx="67" cy="47" r="2.5" fill="#1e293b" />
    {/* subtle smile */}
    <path d="M54 55 Q60 59 66 55" stroke="#c97c5d" strokeWidth="1.5" strokeLinecap="round" fill="none" />
    {/* badge dot */}
    <circle cx="78" cy="75" r="7" fill="#2563eb" />
    <path d="M75 75 L77 77 L81 73" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ══════════════════════════════════════════════════════════════════ */
const SuperAdminLogin: React.FC = () => {
  const navigate = useNavigate();

  /* ── state (unchanged) ──────────────────────────────────────────── */
  const [formData, setFormData] = useState<LoginForm>({ email: '', password: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [noAutofillFocus, setNoAutofillFocus] = useState({ email: false, password: false });
  const [rememberMe, setRememberMe] = useState<boolean>(() => {
    try { return localStorage.getItem('superadmin_remember') === 'true'; } catch { return false; }
  });
  const [backendReachable, setBackendReachable] = useState<boolean | null>(null);

  /* ── effects (unchanged) ────────────────────────────────────────── */
  useEffect(() => {
    document.documentElement.classList.add('sa-login-active');
    return () => document.documentElement.classList.remove('sa-login-active');
  }, []);

  useEffect(() => {
    if (rememberMe) {
      try {
        const saved = localStorage.getItem('superadmin_email');
        if (saved) setFormData((prev) => ({ ...prev, email: saved }));
      } catch { /* ignore */ }
    }
  }, [rememberMe]);

  useEffect(() => {
    const base = getUploadsBaseUrl();
    const url = `${base}/api/v1`;
    const isProduction = /\.onrender\.com|\.vercel\.app|\.railway\.app/i.test(base);
    const timeoutMs = isProduction ? 60000 : 10000;
    const maxRetries = isProduction ? 1 : 4;
    let attempt = 0;
    let cancelled = false;

    const tryPing = () => {
      if (cancelled) return;
      attempt += 1;
      const controller = new AbortController();
      const t = window.setTimeout(() => controller.abort(), timeoutMs);
      fetch(url, { signal: controller.signal, method: 'GET' })
        .then(() => { window.clearTimeout(t); if (!cancelled) setBackendReachable(true); })
        .catch((err) => {
          window.clearTimeout(t);
          if (cancelled) return;
          if (attempt < maxRetries) { window.setTimeout(tryPing, 4000); }
          else {
            const isTimeout = err?.name === 'AbortError';
            if (isProduction || !isTimeout) setBackendReachable(false);
          }
        });
    };

    tryPing();
    return () => { cancelled = true; };
  }, []);

  /* ── validation (unchanged) ─────────────────────────────────────── */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ── submit (unchanged) ─────────────────────────────────────────── */
  const handleSubmit = async (): Promise<void> => {
    if (!validateForm()) return;
    setIsLoading(true);
    setErrors({});

    try {
      if (rememberMe) {
        localStorage.setItem('superadmin_remember', 'true');
        localStorage.setItem('superadmin_email', formData.email);
      } else {
        localStorage.removeItem('superadmin_remember');
        localStorage.removeItem('superadmin_email');
      }

      const data = (await apiService.post('/auth/login-email', {
        email: formData.email,
        password: formData.password,
        login_mode: 'superadmin',
      })) as Record<string, unknown>;

      const roleName = String(data.role_name || '').toLowerCase();
      if (roleName !== 'super admin') {
        throw new Error('Access denied. This login page is for System Owner/Provider only.');
      }

      const extractUsername = (email: string, username?: string) => username || email.split('@')[0];
      const email = String(data.email || formData.email);
      const fullName =
        data.first_name && data.last_name
          ? `${data.first_name} ${data.last_name}`
          : extractUsername(email, data.username as string | undefined);

      const user = {
        id: String(data.user_id ?? ''),
        email,
        username: String(data.username || extractUsername(email)),
        name: fullName,
        roleId: Number(data.role_id) || 1,
        roleName: String(data.role_name || 'Super Admin'),
        permissions: ['all'],
        locationId: (data.location_id as number | null) ?? null,
        locationName: null,
        companyId: data.company_id == null ? null : String(data.company_id),
        profileImagePath: (data.profile_image_path as string | null) ?? null,
      };

      RBACService.setCurrentUser(user);
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('authToken', String(data.access_token));
      localStorage.setItem('userEmail', user.email);
      localStorage.setItem('userRole', user.roleName);
      localStorage.setItem('userRoleId', user.roleId.toString());
      localStorage.setItem('userId', user.id);
      localStorage.setItem('userName', user.name);

      setIsLoading(false);
      navigate('/superadmin');
    } catch (error: unknown) {
      setIsLoading(false);
      const err = error as {
        response?: { status?: number; data?: { detail?: string } };
        message?: string;
        code?: string;
      };
      if (err?.response?.status === 401) {
        setErrors({ general: err?.response?.data?.detail || 'Invalid email or password.' });
      } else if (err?.response?.status === 403) {
        setErrors({ general: err?.response?.data?.detail || 'Access denied.' });
      } else if (err?.message?.includes('Access denied')) {
        setErrors({ general: err.message });
      } else if (err?.message === 'Network Error' || err?.code === 'ERR_NETWORK') {
        const backendUrl =
          (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1').replace(/\/api\/v1\/?$/, '') ||
          'http://localhost:8000';
        setErrors({ general: `Unable to connect. Please check that the backend is running at ${backendUrl}` });
      } else if (err?.code === 'ECONNABORTED' || err?.message?.toLowerCase().includes('timeout')) {
        const backendUrl = getUploadsBaseUrl();
        const isRender = /\.onrender\.com/i.test(backendUrl);
        setErrors({
          general: isRender
            ? `Request timed out. On Render free tier the backend may be starting (cold start can take 1–2 min). Wait a moment, then try again, or open ${backendUrl} in a new tab to wake it up first.`
            : `Request timed out. 1) Start PostgreSQL, then 2) run: cd backend && uvicorn app.main:app --reload. Backend should be at ${backendUrl}.`,
        });
      } else {
        setErrors({ general: err?.message || 'An error occurred. Please try again.' });
      }
    }
  };

  /* ── change handler (unchanged) ─────────────────────────────────── */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const target = e.target as HTMLInputElement;
    const field = (target.dataset.field || target.name) as keyof LoginForm;
    const { value } = target;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors] || errors.general) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as keyof FormErrors];
        delete next.general;
        return next;
      });
    }
  };

  /* ── render ─────────────────────────────────────────────────────── */
  return (
    <div className={styles.page}>
      <PageDecoration />
      {/* ── centred card ── */}
      <div className={styles.card}>

        {/* ── card header (dark blue + wave) ── */}
        <div className={styles.cardHeader}>
          <div className={styles.headerBrand}>
            <div className={styles.headerLogo}>
              <img src={nacLogoUrl} alt="NAC" />
            </div>
            <div className={styles.headerWordmark}>
              <span className={styles.hwName}>NAC<span className={styles.hwPlus}>+</span></span>
              <span className={styles.hwSub}>SUPER ADMIN PORTAL</span>
            </div>
          </div>
          {/* wave divider */}
          <svg className={styles.wave} viewBox="0 0 800 56" preserveAspectRatio="none" aria-hidden>
            <path d="M0,28 C133,56 267,0 400,28 C533,56 667,0 800,28 L800,56 L0,56 Z" fill="#fff" />
          </svg>
        </div>

        {/* ── card body ── */}
        <div className={styles.cardBody}>

          {/* left: avatar column */}
          <div className={styles.avatarCol}>
            <div className={styles.avatarRing}>
              <AvatarIllustration />
            </div>
            <span className={styles.rootBadge}>
              <span className={styles.rootDot} />
              ROOT ACCESS
            </span>
          </div>

          {/* right: form column */}
          <div className={styles.formCol}>
            <div className={styles.formTop}>
              <div>
                <h1 className={styles.formTitle}>Welcome back, Admin</h1>
                <p className={styles.formSub}>Sign in to manage the full system</p>
              </div>
              <span className={styles.superAdminPill}>SUPER ADMIN</span>
            </div>

            <form
              noValidate
              autoComplete="off"
              onSubmit={(e) => { e.preventDefault(); void handleSubmit(); }}
            >
              {errors.general && (
                <div className={styles.errorBanner}>
                  <AlertCircle size={15} />
                  <span>{errors.general}</span>
                </div>
              )}

              {/* user type — read-only display */}
              <div className={styles.field}>
                <label className={styles.fieldLabel}>USER TYPE</label>
                <div className={styles.userTypeDisplay}>Super Admin</div>
              </div>

              {/* email / username */}
              <div className={styles.field}>
                <label className={styles.fieldLabel} htmlFor="sa-email">USERNAME</label>
                <input
                  id="sa-email"
                  className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                  type="text"
                  name="login_email"
                  data-field="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setNoAutofillFocus((p) => ({ ...p, email: true }))}
                  placeholder="you@company.bt"
                  disabled={isLoading}
                  readOnly={!noAutofillFocus.email}
                  autoComplete="username"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-bwignore="true"
                  data-form-type="other"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                />
                {errors.email && <span className={styles.fieldError}>{errors.email}</span>}
              </div>

              {/* password */}
              <div className={styles.field}>
                <label className={styles.fieldLabel} htmlFor="sa-password">PASSWORD</label>
                <div className={styles.passWrap}>
                  <input
                    id="sa-password"
                    className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
                    type={showPassword ? 'text' : 'password'}
                    name="login_password"
                    data-field="password"
                    value={formData.password}
                    onChange={handleChange}
                    onFocus={() => setNoAutofillFocus((p) => ({ ...p, password: true }))}
                    placeholder="••••••••••"
                    disabled={isLoading}
                    readOnly={!noAutofillFocus.password}
                    autoComplete="current-password"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                  />
                  <button
                    type="button"
                    className={styles.eyeBtn}
                    onClick={() => setShowPassword((v) => !v)}
                    disabled={isLoading}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeClosed /> : <EyeOpen />}
                  </button>
                </div>
                {errors.password && <span className={styles.fieldError}>{errors.password}</span>}
              </div>

              {/* remember me */}
              <div className={styles.rememberRow}>
                <label className={styles.checkLabel}>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>Remember me</span>
                </label>
              </div>

              {/* actions */}
              <div className={styles.actions}>
                <button type="submit" className={styles.loginBtn} disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <span className={styles.spinner} />
                      Signing in...
                    </>
                  ) : (
                    <>
                      <ArrowRightIcon />
                      Login
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  disabled={isLoading}
                  onClick={() => { setFormData({ email: '', password: '' }); setErrors({}); }}
                >
                  <ShieldIcon />
                  Cancel
                </button>
              </div>
            </form>

            <p className={styles.auditNote}>
              Every Super Admin session is logged and audited for security.
            </p>

            {backendReachable === false && (
              <p className={styles.backendHint}>
                {/\.onrender\.com/i.test(getUploadsBaseUrl()) ? (
                  <>Backend not reachable at {getUploadsBaseUrl()}. Wait 30 s and refresh.</>
                ) : (
                  <>Backend not reachable. Run: <code>uvicorn app.main:app --reload</code></>
                )}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminLogin;
